'use client';

import { useQueryClient } from '@tanstack/react-query';
import {
  Permission,
  TeamsApi,
  type TeamWithCountsDto,
  WorkspaceMemberRole,
  type WorkspaceMemberWithUserDto,
  type WorkspaceRoleListItemDto,
} from '@up2stay/up2promo-api-client';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Switch,
} from '@up2stay/up2promo-ui';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { canEditWorkspaceMemberRole } from '@/core/authz/can-edit-workspace-member-role';
import {
  assignmentsEqual,
  memberToRoleAssignment,
  roleAssignmentToValue,
  type RoleAssignment,
} from '@/core/authz/role-assignment';
import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import {
  RoleEditor,
  type RoleEditorDict,
} from '@/core/components/workspace/roles/role-editor';
import { RoleSelect } from '@/core/components/workspace/roles/role-select';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { usePermissions } from '@/core/hooks/use-permissions';
import { invalidateUserTeamMembershipQueries } from '@/core/hooks/use-user-team-membership';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';

function getInitials(
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null,
): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) return (firstName[0] ?? '?').toUpperCase();
  if (email) return (email[0] ?? '?').toUpperCase();
  return '?';
}

function getUserDisplayName(
  member: WorkspaceMemberWithUserDto,
  unknownUserLabel: string,
): string {
  const user = member.user;
  if (user?.firstName && user?.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user?.firstName) return user.firstName;
  if (user?.email) return user.email;
  return unknownUserLabel;
}

export type MemberEditSheetDict = {
  title: string;
  description: string;
  roleLabel: string;
  roleDescription: string;
  /** Why the owner role select is locked (own row vs someone else's). */
  ownerRoleLockedSelf: string;
  ownerRoleLocked: string;
  teamsLabel: string;
  teamsDescription: string;
  noTeams: string;
  manageRoles: string;
  createRole: string;
  save: string;
  saving: string;
  cancel: string;
  success: string;
  teamsSuccess: string;
  error: string;
  teamsError: string;
  cannotChangeOwner: string;
  transferOwnership: string;
  remove: string;
  roleSelect: {
    label: string;
    placeholder: string;
    systemGroup: string;
    customGroup: string;
    roles: {
      owner: string;
      admin: string;
      member: string;
    };
  };
  editRoleDialog: {
    update: string;
    updating: string;
    success: string;
    error: string;
  };
  roleEditor: RoleEditorDict;
};

interface MemberEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  member: WorkspaceMemberWithUserDto | null;
  roles: WorkspaceRoleListItemDto[];
  currentUserId?: string;
  currentUserRole?: WorkspaceMemberRole;
  initialTeamIds: string[];
  initialTeamNames?: string[];
  dict: MemberEditSheetDict;
  permissionsDict: Record<string, unknown>;
  builtinRoleLabels: Record<WorkspaceMemberRole, string>;
  builtinRoleDescriptions?: Partial<Record<WorkspaceMemberRole, string>>;
  onManageRoles: () => void;
  onRemove?: () => void;
  onSuccess: () => void;
  updateMemberRole: (
    workspaceId: string,
    memberId: string,
    assignment: RoleAssignment,
  ) => Promise<void>;
}

export function MemberEditSheet({
  open,
  onOpenChange,
  workspaceId,
  member,
  roles,
  currentUserId,
  currentUserRole,
  initialTeamIds,
  initialTeamNames = [],
  dict,
  permissionsDict,
  builtinRoleLabels,
  builtinRoleDescriptions,
  onManageRoles,
  onRemove,
  onSuccess,
  updateMemberRole,
}: MemberEditSheetProps) {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const { isFeatureEnabled } = useEntitlements();
  const canManageRole = can(Permission.MEMBERS_UPDATE_ROLE);
  const canManageTeams = can(Permission.TEAMS_MANAGE);
  const canManageRoles = can(Permission.ROLES_MANAGE);
  const customRolesEnabled = isFeatureEnabled('feature.custom_roles');

  const [teams, setTeams] = useState<TeamWithCountsDto[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [selectedAssignment, setSelectedAssignment] =
    useState<RoleAssignment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roleEditorOpen, setRoleEditorOpen] = useState(false);

  const currentAssignment = useMemo(
    () => (member ? memberToRoleAssignment(member) : null),
    [member],
  );

  const memberSheetKey =
    open && member
      ? [
          member.id,
          initialTeamIds.join(','),
          currentAssignment ? roleAssignmentToValue(currentAssignment) : '',
        ].join('\0')
      : '';
  const [prevMemberSheetKey, setPrevMemberSheetKey] = useState('');
  if (open && member && memberSheetKey !== prevMemberSheetKey) {
    setPrevMemberSheetKey(memberSheetKey);
    setSelectedAssignment(currentAssignment);
    setSelectedTeamIds(initialTeamIds);
  } else if (!open && prevMemberSheetKey !== '') {
    setPrevMemberSheetKey('');
  }

  useEffect(() => {
    if (!open || !canManageTeams || !workspaceId) return;
    let cancelled = false;
    void (async () => {
      setTeamsLoading(true);
      try {
        const res = await TeamsApi.findAll({
          path: { workspaceId },
          query: { page: 1, limit: 100 },
        });
        if (cancelled) return;
        setTeams((res.data?.data ?? []) as TeamWithCountsDto[]);
      } catch {
        if (!cancelled) setTeams([]);
      } finally {
        if (!cancelled) setTeamsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, canManageTeams, workspaceId]);

  if (
    !member ||
    !currentAssignment ||
    !selectedAssignment ||
    !currentUserRole
  ) {
    return null;
  }

  const displayName = getUserDisplayName(member, dict.title);
  const user = member.user;
  const initials = getInitials(user?.firstName, user?.lastName, user?.email);

  const isOwnerMember =
    currentAssignment.kind === 'builtin' &&
    currentAssignment.role === WorkspaceMemberRole.OWNER;
  const selectingOwner =
    selectedAssignment.kind === 'builtin' &&
    selectedAssignment.role === WorkspaceMemberRole.OWNER;
  const transferringOwnership =
    selectingOwner &&
    !(
      currentAssignment.kind === 'builtin' &&
      currentAssignment.role === WorkspaceMemberRole.OWNER
    );
  const canEditRoleForMember = canEditWorkspaceMemberRole({
    canManageRole,
    memberUserId: member.userId,
    memberRole: member.role,
    currentUserId,
    currentUserRole,
  });
  const roleSelectDisabled = !canEditRoleForMember;
  const roleHint = !roleSelectDisabled
    ? dict.roleDescription
    : isOwnerMember
      ? member.userId === currentUserId
        ? dict.ownerRoleLockedSelf
        : dict.ownerRoleLocked
      : undefined;

  const teamsChanged =
    selectedTeamIds.length !== initialTeamIds.length ||
    selectedTeamIds.some((id) => !initialTeamIds.includes(id));
  const roleChanged = !assignmentsEqual(selectedAssignment, currentAssignment);
  const hasChanges = roleChanged || teamsChanged;

  const syncTeamMembership = async () => {
    const initial = new Set(initialTeamIds);
    const selected = new Set(selectedTeamIds);
    const toAdd = selectedTeamIds.filter((id) => !initial.has(id));
    const toRemove = initialTeamIds.filter((id) => !selected.has(id));

    for (const teamId of toAdd) {
      await TeamsApi.addMember({
        path: { workspaceId, teamId },
        body: { workspaceMemberId: member.id },
      });
    }

    for (const teamId of toRemove) {
      const membersRes = await TeamsApi.listMembers({
        path: { workspaceId, teamId },
      });
      const teamMembers = membersRes.data ?? [];
      const teamMember = teamMembers.find((m) => m.userId === member.userId);
      if (teamMember?.id) {
        await TeamsApi.removeMember({
          path: { workspaceId, teamId, teamMemberId: teamMember.id },
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (!hasChanges) {
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);
    try {
      if (roleChanged) {
        if (!canEditRoleForMember) {
          toast.error(dict.cannotChangeOwner);
          return;
        }
        if (selectingOwner && currentUserRole !== WorkspaceMemberRole.OWNER) {
          toast.error(dict.cannotChangeOwner);
          return;
        }
        if (isOwnerMember && !selectingOwner) {
          toast.error(dict.cannotChangeOwner);
          return;
        }
        await updateMemberRole(workspaceId, member.id, selectedAssignment);
        toast.success(dict.editRoleDialog.success);
      }

      if (teamsChanged && canManageTeams) {
        await syncTeamMembership();
        invalidateUserTeamMembershipQueries(queryClient, workspaceId);
        toast.success(dict.teamsSuccess);
      }

      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to update member:', error);
      toast.error(roleChanged ? dict.editRoleDialog.error : dict.teamsError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const readOnly = !canEditRoleForMember && !canManageTeams;

  return (
    <>
      <ResponsiveDialog
        open={open}
        onOpenChange={(next) => {
          void triggerTapHaptic();
          onOpenChange(next);
        }}
      >
        <ResponsiveDialogContent className="sm:max-w-[500px]">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>{dict.title}</ResponsiveDialogTitle>
            <ResponsiveDialogDescription className="sr-only">
              {dict.description.replace('{name}', displayName)}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="flex items-center gap-3 py-1">
            <Avatar className="h-12 w-12">
              <AvatarImage
                src={getAbsoluteFileUrl(user?.photo?.path)}
                alt={displayName}
              />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate font-medium">{displayName}</p>
              {user?.email ? (
                <p className="truncate text-sm text-muted-foreground">
                  {user.email}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-4 py-1">
            <SettingsGroup>
              <SettingsGroupRows>
                <SettingsGroupRow>
                  <div className="space-y-2">
                    <div className="flex items-center gap-0.5">
                      <Label htmlFor="member-role">
                        {dict.roleSelect.label}
                      </Label>
                      {canManageRole && roleHint ? (
                        <SettingsInfoHint label={dict.roleSelect.label}>
                          {roleHint}
                        </SettingsInfoHint>
                      ) : null}
                    </div>
                    <RoleSelect
                      id="member-role"
                      value={selectedAssignment}
                      onChange={setSelectedAssignment}
                      roles={roles}
                      currentUserRole={currentUserRole}
                      dict={dict.roleSelect}
                      disabled={roleSelectDisabled || readOnly}
                    />
                    {transferringOwnership ? (
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        {dict.transferOwnership}
                      </p>
                    ) : null}
                    {canManageRoles ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto px-0"
                          onClick={() => {
                            onOpenChange(false);
                            onManageRoles();
                          }}
                        >
                          {dict.manageRoles}
                          <ExternalLink className="ml-1 h-3.5 w-3.5" />
                        </Button>
                        {customRolesEnabled ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-auto px-0"
                            onClick={() => setRoleEditorOpen(true)}
                          >
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            {dict.createRole}
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </SettingsGroupRow>
              </SettingsGroupRows>
            </SettingsGroup>

            {(canManageTeams || initialTeamIds.length > 0) && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-0.5 px-1">
                  <Label className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                    {dict.teamsLabel}
                  </Label>
                  {canManageTeams && dict.teamsDescription ? (
                    <SettingsInfoHint label={dict.teamsLabel}>
                      {dict.teamsDescription}
                    </SettingsInfoHint>
                  ) : null}
                </div>
                {teamsLoading ? (
                  <p className="px-1 text-sm text-muted-foreground">
                    {dict.noTeams}
                  </p>
                ) : canManageTeams ? (
                  <SettingsGroup>
                    <SettingsGroupRows>
                      {teams.length === 0 ? (
                        <SettingsGroupRow>
                          <p className="text-sm text-muted-foreground">
                            {dict.noTeams}
                          </p>
                        </SettingsGroupRow>
                      ) : (
                        teams.map((team, index) => {
                          const checked = selectedTeamIds.includes(team.id);
                          return (
                            <SettingsGroupRow
                              key={team.id}
                              separated={index > 0}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <Label
                                  htmlFor={`team-${team.id}`}
                                  className="font-normal"
                                >
                                  {team.name}
                                </Label>
                                <Switch
                                  id={`team-${team.id}`}
                                  checked={checked}
                                  onCheckedChange={(next) => {
                                    setSelectedTeamIds((prev) =>
                                      next
                                        ? [...prev, team.id]
                                        : prev.filter((id) => id !== team.id),
                                    );
                                  }}
                                />
                              </div>
                            </SettingsGroupRow>
                          );
                        })
                      )}
                    </SettingsGroupRows>
                  </SettingsGroup>
                ) : (
                  <p className="px-1 text-sm text-muted-foreground">
                    {initialTeamNames.length > 0
                      ? initialTeamNames.join(', ')
                      : dict.noTeams}
                  </p>
                )}
              </div>
            )}
          </div>

          <ResponsiveDialogFooter className="flex-col gap-2 sm:flex-row">
            {onRemove ? (
              <Button
                type="button"
                variant="ghost"
                className="mr-auto text-destructive hover:text-destructive"
                onClick={() => {
                  onOpenChange(false);
                  onRemove();
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {dict.remove}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {dict.cancel}
            </Button>
            {!readOnly ? (
              <Button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={isSubmitting || !hasChanges}
              >
                {isSubmitting ? dict.saving : dict.save}
              </Button>
            ) : null}
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      {canManageRoles && customRolesEnabled ? (
        <RoleEditor
          open={roleEditorOpen}
          onOpenChange={setRoleEditorOpen}
          workspaceId={workspaceId}
          role={null}
          dict={dict.roleEditor}
          permissionsDict={permissionsDict}
          builtinRoleLabels={builtinRoleLabels}
          builtinRoleDescriptions={builtinRoleDescriptions}
          onSuccess={onSuccess}
          canEditCustomRoles={customRolesEnabled}
        />
      ) : null}
    </>
  );
}
